import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { 
  Building2, 
  Search, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  User,
  CheckCircle2,
  Loader2,
  Briefcase
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyRegistration() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [orgNumber, setOrgNumber] = useState("");
  const [companyFound, setCompanyFound] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    org_number: "",
    vat_number: "",
    address: "",
    city: "",
    postal_code: "",
    country: "Norway",
    contact_person_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: user?.phone || "",
    website: "",
    client_type: user?.client_type || "seeker",
  });

  const handleLookup = async () => {
    if (!orgNumber.trim()) {
      toast.error("Please enter organization number");
      return;
    }

    setLookingUp(true);
    try {
      const response = await axios.get(`${API}/lookup/company/${orgNumber.trim()}`);
      
      if (response.data.found) {
        setFormData({
          ...formData,
          name: response.data.name,
          org_number: response.data.org_number,
          address: response.data.address,
          city: response.data.city,
          postal_code: response.data.postal_code,
          country: response.data.country || "Norway",
          website: response.data.website || "",
        });
        setCompanyFound(true);
        toast.success(`Found: ${response.data.name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Company not found");
      setCompanyFound(false);
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.org_number || !formData.name || !formData.address || 
        !formData.city || !formData.postal_code || !formData.contact_person_name ||
        !formData.contact_email || !formData.contact_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/companies`, {
        ...formData,
        company_type: "client",
      });

      toast.success("Company registered successfully!");
      
      // Update user context with company_id
      setUser({ ...user, company_id: response.data.company_id });
      
      // Navigate to appropriate dashboard
      navigate("/client");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#23F7DD] rounded-lg flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-[#050505]" />
          </div>
          <h1 className="text-3xl font-['Unbounded'] font-bold text-white mb-2">
            Register Your Company
          </h1>
          <p className="text-[#94A3B8]">
            All companies must be registered in Norway to use AllStillas
          </p>
        </div>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">
              Company Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Org Number Lookup */}
              <div className="p-4 bg-[#23F7DD]/5 rounded-lg border border-[#23F7DD]/30">
                <Label className="text-white mb-2 block">
                  Norwegian Organization Number (Org.nr)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={orgNumber}
                    onChange={(e) => setOrgNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    placeholder="9 digits, e.g., 923609016"
                    maxLength={9}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD] font-mono text-lg"
                  />
                  <Button
                    type="button"
                    onClick={handleLookup}
                    disabled={lookingUp || orgNumber.length !== 9}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] px-6"
                  >
                    {lookingUp ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Lookup
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Enter your org number to auto-fill company details from Brønnøysund Register
                </p>
                {companyFound && (
                  <div className="mt-3 flex items-center gap-2 text-green-400">
                    <CheckCircle2 className="w-4 h-4" />
                    Company found and details filled
                  </div>
                )}
              </div>

              {/* Company Name */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Company Name <span className="text-red-400">*</span></Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Company AS"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  required
                />
              </div>

              {/* VAT Number */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">VAT Number (MVA) - Optional</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  placeholder="NO 123 456 789 MVA"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Address <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">Postal Code <span className="text-red-400">*</span></Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="0001"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">City <span className="text-red-400">*</span></Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Oslo"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Website - Optional
                </Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.company.no"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>

              {/* Company Type Selection */}
              <div className="space-y-3 p-4 bg-white/5 rounded-lg">
                <Label className="text-white font-medium">What does your company need?</Label>
                <RadioGroup
                  value={formData.client_type}
                  onValueChange={(value) => setFormData({ ...formData, client_type: value })}
                  className="space-y-3"
                >
                  <div className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.client_type === "seeker" 
                      ? "border-[#23F7DD] bg-[#23F7DD]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}>
                    <label htmlFor="seeker" className="cursor-pointer flex items-start gap-3">
                      <RadioGroupItem
                        value="seeker"
                        id="seeker"
                        className="mt-1 border-[#23F7DD] text-[#23F7DD]"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-[#23F7DD]" />
                          <span className="text-white font-medium">Looking for Scaffolding</span>
                        </div>
                        <p className="text-sm text-[#94A3B8] mt-1">
                          Post projects and receive bids from scaffolding companies
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  <div className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.client_type === "provider" 
                      ? "border-[#FF5F1F] bg-[#FF5F1F]/10" 
                      : "border-white/10 hover:border-white/30"
                  }`}>
                    <label htmlFor="provider" className="cursor-pointer flex items-start gap-3">
                      <RadioGroupItem
                        value="provider"
                        id="provider"
                        className="mt-1 border-[#FF5F1F] text-[#FF5F1F]"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-[#FF5F1F]" />
                          <span className="text-white font-medium">Scaffolding Company</span>
                        </div>
                        <p className="text-sm text-[#94A3B8] mt-1">
                          Browse projects and submit bids to win contracts
                        </p>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Contact Person */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#23F7DD]" />
                  Contact Person
                </h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#94A3B8]">Full Name <span className="text-red-400">*</span></Label>
                    <Input
                      value={formData.contact_person_name}
                      onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                      placeholder="John Doe"
                      className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#94A3B8] flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        placeholder="contact@company.no"
                        className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#94A3B8] flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        placeholder="+47 123 45 678"
                        className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Registering...
                  </span>
                ) : (
                  <>
                    <Building2 className="w-5 h-5 mr-2" />
                    Register Company
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
